import React from 'react';
import PropTypes from 'prop-types';
import {Button, Icon, Label, Table} from 'semantic-ui-react';
import { saveAs } from 'file-saver';
import { getIn } from 'formik';
import AdvancedFieldError from '../FieldError/index2';
import { withTranslation } from 'react-i18next';
import InputWithLabelWrapper from '../InputWithLabelWrapper';
import ImageViewerModal from "../ImageViewerModal";
import {getObjPropRecursively} from "../../utils";
import {filesActions} from "../../behavior/files";
import { connect } from 'react-redux';
import { compose } from 'redux';

const TableFileViewerWrapper = ({ ...props }) => {
	const getUploadedFilesTable = () => {
		const { disabled, value, filesHeaderIcon } = props;

		return (
			<Table basic className="reducedWhiteSpaceTableHeader">
				<Table.Header>
					<Table.Row>
						{!disabled && <Table.HeaderCell style={{ width: '30px' }} />}
						<Table.HeaderCell>
							{filesHeaderIcon && <Icon name={filesHeaderIcon} size="small" />}
							<InputWithLabelWrapper label="file_input_file_name" fieldClassName="inlineBlock"/>
						</Table.HeaderCell>
						{getExtraColumnsHeaders()}
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{value.map((f, index) => {
						return getUploadedFileRow(f, index);
					})}
				</Table.Body>
			</Table>
		);
	};

	const getExtraColumnsHeaders = () => {
		const { extraColumns, t } = props;
		if (extraColumns)
			return extraColumns.map(c => (
				<Table.HeaderCell key={`tableFileViewerWrapperHeader-${c.label}`}>
					<InputWithLabelWrapper label={c && c.label ? t(c.label) : null} />
				</Table.HeaderCell>
			));
		else return null;
	};

	const getDeleteCell = index => {
		const { disabled, value, onChangeValue } = props;

		return (
			!disabled && (
				<Table.Cell className="tableCellOption">
					<div>
						<Button
							icon="remove"
							color="red"
							className="mouseCursorPointer mini"
							onClick={() => onChangeValue(value, true, index)}
						/>
					</div>
				</Table.Cell>
			)
		);
	};

	const getDownloadFileCell = (f, index) => {
		const {
			errors,
			value,
			name,
			hideFile,
			errorPaths,
			fileDataProp,
			api
		} = props;
		const errorMessages = errorPaths.map(path => getIn(errors, name + `[${index}]${path}`));
		const fileData = getObjPropRecursively(f, fileDataProp);

		const imageFilesToPreview = value.filter(
			vf => fileData.fileType == 'Image' && getObjPropRecursively(vf, fileDataProp).fileType == 'Image' && !hideFile(vf)
		);

		return (
			<Table.Cell className="tableCellOption">
				{fileData.fileType == 'Image' && (
					<ImageViewerModal
						file={fileData}
						fileList={imageFilesToPreview.map(f => getObjPropRecursively(f, fileDataProp))}
						compactTrigger={false}
						api={api}
					/>
				)}
				<a
					href=""
					onClick={e => {
						e.preventDefault();
						downloadFile(fileData);
					}}
					className={errorMessages != null ? 'error' : ''}
				>
					<AdvancedFieldError messages={errorMessages} />
					<Label
						basic
						title={fileData.name}
						key={'sds_label_' + index}
						style={{ fontWeight: 'normal' }}
					>
						{f.id && (
							<Icon name="check circle outline" color="green" key={'file_icon_saved' + index} />
						)}
						{(fileData.fileType == 'Pdf' || fileData.name.endsWith('.pdf')) && (
							<Icon name="file pdf" color="red" key={'file_icon_' + index} />
						)}
						{(fileData.fileType == 'Image' ||
							/png$|gif$|jpg$|jpe$|jpeg$|bmp$/.test(fileData.name)) && (
							<Icon name="file image" color="blue" key={'file_icon_' + index} />
						)}
						{' ' +
						(fileData.name.length > 20
							? fileData.name.substring(0, 20) + '...'
							: fileData.name) +
						' '}
					</Label>
				</a>
			</Table.Cell>
		);
	};

	const getExtraColumnsCells = (f, index) => {
		const { extraColumns } = props;
		return (
			extraColumns &&
			extraColumns.map(c => (
				<Table.Cell
					className="tableCellOption"
					width={c.width}
					key={`tableFileViewerWrapperCell-${c.label}-${index}`}
				>
					{c.render({ ...props, f, fIndex: index })}
				</Table.Cell>
			))
		);
	};

	/**
	 * Displays the uploaded files' names, allowing download, deletion or showing validation messages
	 * @returns {Array}
	 */
	const getUploadedFileRow = (f, index) => {
		const { hideFile } = props;

		return (
			<Table.Row key={index} hidden={hideFile(f)}>
				{getDeleteCell(index)}
				{getDownloadFileCell(f, index)}
				{getExtraColumnsCells(f, index)}
			</Table.Row>
		);
	};

	const downloadFile = f => {
		//request it from server if saved
		if (f.id) props.download(props.api, f.id);
		//else immediate download
		else saveAs(f.file, f.name);
	};

	return getUploadedFilesTable();
};

TableFileViewerWrapper.propTypes = {
	name: PropTypes.string.isRequired,
	disabled: PropTypes.bool.isRequired,
	errors: PropTypes.object.isRequired,
	value: PropTypes.array.isRequired,
	extraColumns: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string,
			render: PropTypes.func.isRequired,
			width: PropTypes.number
		})
	)
};

TableFileViewerWrapper.defaultProps = {
	disabled: false,
	hideFile: f => {
		return false;
	},
	fileDataProp: '',
	errorPaths: []
};


const mapDispatchToProps = dispatch => ({
	download: (api, fileId) => dispatch(filesActions.download(api, fileId))
});

export default compose(
	withTranslation(),
	connect(null, mapDispatchToProps)
)(TableFileViewerWrapper);
